import { useState, useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { slideInRight } from '@/lib/motion'
import { useBodyScrollLock } from '@/components/ui/Modal'
import { Conversation } from '@11labs/client'
import {
  Phone,
  PhoneCall,
  Mic,
  MicOff,
  Zap,
  Copy,
  Check,
  RefreshCw,
  PowerOff,
  Clock,
  ChevronDown,
  ChevronUp,
  User,
  Bot,
  Loader2,
  ArrowRight,
  ArrowLeft,
  MapPin,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Volume2,
  X,
  CalendarCheck,
  ShoppingBag,
  ShieldCheck,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { useMyRestaurant } from '@/lib/queries/useRestaurants'
import { STRAPI_BASE_URL as STRAPI_URL } from '@/config/api'
import { useBookingOpeningHours } from '@/lib/booking/scheduleAdapter'
import { useBookings } from '@/lib/queries/useBookings'
import { storage } from '@/utils/storage'
import { toast } from 'sonner'
import {
  useProvisionSheAnswers,
  useDeprovisionSheAnswers,
  useSyncSheAnswers,
  useSheAnswersConversations,
  useSheAnswersConversation,
  useAgentConfig,
  useUpdateAgentConfig,
  type SheAnswersConversation,
} from '@/lib/queries/useSheAnswers'

// ── Helpers ────────────────────────────────────────────────────

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function formatTimestamp(unixSecs: number): string {
  return new Date(unixSecs * 1000).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

const DAY_NAMES = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// ── Voice options ──────────────────────────────────────────────

const VOICE_OPTIONS = [
  {
    voiceId: 'nBGpwX2gxJKzCm2Z6Oxj',
    name: 'Bec',
    tag: 'Receptionist',
    accent: 'British',
    gender: 'Female',
    description: 'Polished and professional — purpose-built for front-of-house calls.',
  },
  {
    voiceId: 'EXAVITQu4vr4xnSDxMaL',
    name: 'Sarah',
    tag: 'Reassuring',
    accent: 'American',
    gender: 'Female',
    description: 'Mature, calm and confident — puts callers immediately at ease.',
  },
  {
    voiceId: 'XrExE9yKIg1WjnnlVkGX',
    name: 'Matilda',
    tag: 'Professional',
    accent: 'American',
    gender: 'Female',
    description: 'Knowledgeable and clear — great for information-heavy conversations.',
  },
  {
    voiceId: 'hpp4J3VqNfWAUOO0d1Us',
    name: 'Bella',
    tag: 'Warm',
    accent: 'American',
    gender: 'Female',
    description: 'Bright, warm and approachable — feels like talking to a real team member.',
  },
  {
    voiceId: 'hmMWXCj9K7N5mCPcRkfC',
    name: 'Rory',
    tag: 'Irish',
    accent: 'Irish',
    gender: 'Male',
    description: 'Friendly Irish accent — natural and conversational for local restaurants.',
  },
]

const DEFAULT_VOICE_ID = VOICE_OPTIONS[0].voiceId

// Module-level ref so we can stop the current track from any call
let _currentAudio: HTMLAudioElement | null = null
let _currentAudioUrl: string | null = null

function stopCurrentAudio(setPlaying: (id: string | null) => void) {
  if (_currentAudio) {
    _currentAudio.pause()
    _currentAudio.src = ''
    _currentAudio = null
  }
  if (_currentAudioUrl) {
    URL.revokeObjectURL(_currentAudioUrl)
    _currentAudioUrl = null
  }
  setPlaying(null)
}

async function playVoicePreview(
  voiceId: string,
  playingVoiceId: string | null,
  setLoading: (id: string | null) => void,
  setPlaying: (id: string | null) => void,
) {
  // Toggle: clicking the playing voice stops it
  if (playingVoiceId === voiceId) {
    stopCurrentAudio(setPlaying)
    return
  }
  // Stop any other voice that's playing or loading
  stopCurrentAudio(setPlaying)

  setLoading(voiceId)
  try {
    const token = storage.getToken()
    const res = await fetch(`${STRAPI_URL}/api/she-answers/voice-preview/${voiceId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body?.error?.message ?? `Preview failed (${res.status})`)
    }
    const json = await res.json()
    const bytes = Uint8Array.from(atob(json.data.audio), (c) => c.charCodeAt(0))
    const blob = new Blob([bytes], { type: 'audio/mpeg' })
    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    _currentAudio = audio
    _currentAudioUrl = url
    audio.onended = () => {
      URL.revokeObjectURL(url)
      _currentAudio = null
      _currentAudioUrl = null
      setPlaying(null)
    }
    setPlaying(voiceId)
    await audio.play()
  } catch (err: any) {
    toast.error(err?.message ?? 'Could not play voice preview')
    setPlaying(null)
  } finally {
    setLoading(null)
  }
}

function SoundWave() {
  const heights = [4, 10, 7, 12, 5, 9, 4]
  return (
    <div className="flex items-center gap-px h-4">
      {heights.map((h, i) => (
        <motion.div
          key={i}
          className="w-0.5 rounded-full bg-purple-400"
          animate={{ height: [h * 0.5, h, h * 0.5] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.07, ease: 'easeInOut' }}
          style={{ height: h }}
        />
      ))}
    </div>
  )
}

// ── Activation Wizard ──────────────────────────────────────────

function ActivationWizard({
  onActivate,
  isPending,
}: {
  onActivate: (voiceId: string) => void
  isPending: boolean
}) {
  const [step, setStep] = useState(1)
  const [selectedVoiceId, setSelectedVoiceId] = useState(DEFAULT_VOICE_ID)
  const [previewLoading, setPreviewLoading] = useState<string | null>(null)
  const [previewPlaying, setPreviewPlaying] = useState<string | null>(null)

  const { data: restaurantRes } = useMyRestaurant()
  const { data: ohRes } = useBookingOpeningHours()

  const restaurant = Array.isArray((restaurantRes as any)?.data)
    ? (restaurantRes as any).data[0]
    : null
  const openingHours: any[] = (ohRes as any)?.data ?? []
  const activeOH = openingHours.filter((oh: any) => !oh.special)

  const hasPhone = !!restaurant?.phone
  const hasAddress = !!(restaurant?.address?.city || restaurant?.address?.address1)
  const hasHours = activeOH.length > 0

  const STEPS = ['How it works', 'Choose voice', 'Review setup', 'Confirm']

  return (
    <Card>
      {/* Step indicator */}
      <CardHeader className="pb-3 border-b border-[var(--color-border-subtle)]">
        <div className="flex items-center gap-0">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex items-center">
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  step === n
                    ? 'bg-purple-600 text-white'
                    : step > n
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]'
                }`}
              >
                {step > n ? <Check className="h-3.5 w-3.5" /> : n}
              </div>
              {n < 4 && (
                <div
                  className={`h-px w-6 mx-1 transition-colors ${
                    step > n ? 'bg-purple-500/40' : 'bg-[var(--color-border-subtle)]'
                  }`}
                />
              )}
            </div>
          ))}
          <span className="ml-3 text-xs text-[var(--color-text-muted)]">
            {STEPS[step - 1]}
          </span>
        </div>
      </CardHeader>

      <CardContent className="pt-5">

        {/* ── Step 1: How it works ── */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
                Your AI phone receptionist
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)]">
                SheAnswers picks up every call to your restaurant, answers questions about
                availability, and books tables — 24 hours a day, with no human needed.
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  icon: Phone,
                  title: 'A dedicated phone number',
                  desc: 'You get a real phone number to add to your Google listing, website, and social profiles.',
                },
                {
                  icon: Calendar,
                  title: 'Checks availability in real time',
                  desc: 'The agent calls your booking system to confirm open slots before offering them to the caller.',
                },
                {
                  icon: Mic,
                  title: 'Creates bookings automatically',
                  desc: 'Once the guest confirms, SheAnswers creates the reservation directly in your bookings.',
                },
                {
                  icon: Zap,
                  title: 'Stays up to date',
                  desc: 'Opening hours, restaurant info, and availability sync automatically whenever you make changes.',
                },
              ].map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="flex gap-3 p-3 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)]"
                >
                  <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-primary)]">{title}</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end pt-1">
              <Button
                onClick={() => setStep(2)}
                className="bg-purple-600 hover:bg-purple-500 text-white"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Choose voice ── */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
                Choose your agent's voice
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)]">
                This is the voice your customers will hear when they call. Click{' '}
                <Volume2 className="h-3.5 w-3.5 inline-block mb-0.5" /> to hear a sample.
              </p>
            </div>

            <div className="space-y-2">
              {VOICE_OPTIONS.map((v) => {
                const isSelected = selectedVoiceId === v.voiceId
                const isLoadingPreview = previewLoading === v.voiceId
                const isPlayingPreview = previewPlaying === v.voiceId
                return (
                  <button
                    key={v.voiceId}
                    onClick={() => setSelectedVoiceId(v.voiceId)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      isSelected
                        ? 'border-purple-500/60 bg-purple-500/8'
                        : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-default)] bg-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Selection ring */}
                        <div
                          className={`h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                            isSelected
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-[var(--color-border-default)]'
                          }`}
                        >
                          {isSelected && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                              {v.name}
                            </span>
                            <span className="px-1.5 py-0.5 rounded text-[0.6rem] font-bold uppercase tracking-wide bg-purple-500/15 text-purple-400">
                              {v.tag}
                            </span>
                            <span className="text-xs text-[var(--color-text-muted)]">
                              {v.accent} · {v.gender}
                            </span>
                          </div>
                          <p className="text-xs text-[var(--color-text-muted)] mt-0.5 truncate">
                            {v.description}
                          </p>
                        </div>
                      </div>

                      {/* Preview button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          playVoicePreview(v.voiceId, previewPlaying, setPreviewLoading, setPreviewPlaying)
                        }}
                        disabled={isLoadingPreview}
                        className={`h-8 w-8 shrink-0 rounded-lg flex items-center justify-center transition-colors disabled:opacity-50 ${
                          isPlayingPreview
                            ? 'bg-purple-500/10 text-purple-400'
                            : 'hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-purple-400'
                        }`}
                        title={isPlayingPreview ? 'Stop preview' : 'Preview voice'}
                      >
                        {isLoadingPreview ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isPlayingPreview ? (
                          <SoundWave />
                        ) : (
                          <Volume2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </button>
                )
              })}
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button variant="secondary" onClick={() => setStep(1)}>
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="bg-purple-600 hover:bg-purple-500 text-white"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Review setup ── */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
                Review your restaurant info
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)]">
                SheAnswers uses this data to answer caller questions. Make sure it's complete before
                activating.
              </p>
            </div>

            <div className="space-y-2">
              {/* Restaurant name */}
              <div className="p-3 rounded-lg border border-[var(--color-border-subtle)] space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                  Restaurant
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-text-primary)]">
                    {restaurant?.name ?? '—'}
                  </span>
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                </div>
              </div>

              {/* Phone */}
              <div
                className={`p-3 rounded-lg border space-y-1 ${
                  hasPhone
                    ? 'border-[var(--color-border-subtle)]'
                    : 'border-amber-500/30 bg-amber-500/5'
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                  Phone number
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-text-primary)]">
                    {hasPhone ? (
                      restaurant.phone
                    ) : (
                      <span className="text-amber-400">Not set — add in Settings</span>
                    )}
                  </span>
                  {hasPhone ? (
                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-400" />
                  )}
                </div>
              </div>

              {/* Address */}
              <div
                className={`p-3 rounded-lg border space-y-1 ${
                  hasAddress
                    ? 'border-[var(--color-border-subtle)]'
                    : 'border-amber-500/30 bg-amber-500/5'
                }`}
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                  Address
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-text-primary)]">
                    {hasAddress ? (
                      [restaurant?.address?.address1, restaurant?.address?.city]
                        .filter(Boolean)
                        .join(', ')
                    ) : (
                      <span className="text-amber-400">Not set — add in Settings</span>
                    )}
                  </span>
                  {hasAddress ? (
                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-400" />
                  )}
                </div>
              </div>

              {/* Opening hours */}
              <div
                className={`p-3 rounded-lg border space-y-2 ${
                  hasHours
                    ? 'border-[var(--color-border-subtle)]'
                    : 'border-red-500/30 bg-red-500/5'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                    Opening hours
                  </p>
                  {hasHours ? (
                    <CheckCircle2 className="h-4 w-4 text-green-400" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-400" />
                  )}
                </div>
                {hasHours ? (
                  <div className="flex flex-wrap gap-1.5">
                    {activeOH.map((oh: any) => (
                      <span
                        key={oh.id}
                        className="px-2 py-0.5 rounded text-xs bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]"
                      >
                        {DAY_NAMES[oh.day] ?? `Day ${oh.day}`} {oh.open}–{oh.close}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-red-400">
                    No opening hours set — the agent won't be able to check availability.{' '}
                    <a href="/opening-hours" className="underline">
                      Set them up first.
                    </a>
                  </p>
                )}
              </div>

              {/* Selected voice summary */}
              <div className="p-3 rounded-lg border border-[var(--color-border-subtle)] space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                  Voice
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-text-primary)]">
                    {VOICE_OPTIONS.find((v) => v.voiceId === selectedVoiceId)?.name ?? 'Bec'}
                  </span>
                  <button
                    onClick={() => setStep(2)}
                    className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    Change
                  </button>
                </div>
              </div>

              <div className="p-3 rounded-lg border border-purple-500/30 bg-purple-500/5 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                  Phone mode
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[var(--color-text-primary)]">
                    Browser test calls only
                  </span>
                  <span className="flex items-center gap-1 text-xs text-purple-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-purple-400" />
                    Test mode
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button variant="secondary" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back
              </Button>
              <Button
                onClick={() => setStep(4)}
                disabled={!hasHours}
                className="bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-50"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 4: Confirm & activate ── */}
        {step === 4 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
                Ready to activate
              </h3>
              <p className="text-sm text-[var(--color-text-secondary)]">
                Clicking "Activate SheAnswers" will:
              </p>
            </div>

            <div className="space-y-2 text-sm">
              {[
                'Create a dedicated AI voice agent for your restaurant',
                `Use the ${VOICE_OPTIONS.find((v) => v.voiceId === selectedVoiceId)?.name ?? 'selected'} voice for all calls`,
                'Create the agent for browser test calls without assigning a phone number',
                'Upload your restaurant info and opening hours as a knowledge base',
                'Keep customer phone calls disabled until an admin assigns a number from the platform',
              ].map((item) => (
                <div key={item} className="flex gap-2.5 items-start">
                  <CheckCircle2 className="h-4 w-4 text-purple-400 shrink-0 mt-0.5" />
                  <span className="text-[var(--color-text-secondary)]">{item}</span>
                </div>
              ))}
            </div>

            <div className="p-3 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] flex gap-2.5">
              <MapPin className="h-4 w-4 text-[var(--color-text-muted)] shrink-0 mt-0.5" />
              <p className="text-xs text-[var(--color-text-muted)]">
                The agent will stay in test mode after activation. A platform admin can assign a Twilio number later from /platform.
              </p>
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button variant="secondary" onClick={() => setStep(3)} disabled={isPending}>
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back
              </Button>
              <Button
                onClick={() => onActivate(selectedVoiceId)}
                disabled={isPending}
                className="bg-purple-600 hover:bg-purple-500 text-white"
              >
                {isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Activating…
                  </>
                ) : (
                  <>
                    <Phone className="h-4 w-4 mr-2" />
                    Activate SheAnswers
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Agent Settings Card ─────────────────────────────────────────

const MOOD_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'cheerful',     label: 'Cheerful' },
  { value: 'calm',         label: 'Calm' },
  { value: 'warm',         label: 'Warm' },
  { value: 'energetic',    label: 'Energetic' },
]

function AgentToggle({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: () => void
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${
        checked ? 'bg-purple-600' : 'bg-[var(--color-bg-elevated)]'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

function AgentSettingsCard() {
  const { data: config, isLoading } = useAgentConfig(true)
  const update = useUpdateAgentConfig()

  const [greeting, setGreeting] = useState('')
  const [selectedVoice, setSelectedVoice] = useState('')
  const [selectedMood, setSelectedMood] = useState('professional')
  const [allowBookings, setAllowBookings] = useState(false)
  const [allowOrders, setAllowOrders] = useState(false)
  const [allowPromptOverwrite, setAllowPromptOverwrite] = useState(true)
  const [previewLoading, setPreviewLoading] = useState<string | null>(null)
  const [previewPlaying, setPreviewPlaying] = useState<string | null>(null)
  const [initialized, setInitialized] = useState(false)

  // Populate form once config loads
  useEffect(() => {
    if (!config || initialized) return
    setGreeting(config.firstMessage)
    setSelectedVoice(config.voiceId)
    setSelectedMood(config.mood ?? 'professional')
    setAllowBookings(config.allowBookings ?? false)
    setAllowOrders(config.allowOrders ?? false)
    setAllowPromptOverwrite(config.allowPromptOverwrite ?? true)
    setInitialized(true)
  }, [config, initialized])

  function handleSave() {
    const patch: {
      firstMessage?: string
      voiceId?: string
      mood?: string
      allowBookings?: boolean
      allowOrders?: boolean
      allowPromptOverwrite?: boolean
    } = {}
    if (greeting !== config?.firstMessage) patch.firstMessage = greeting
    if (selectedVoice !== config?.voiceId) patch.voiceId = selectedVoice
    if (selectedMood !== config?.mood) patch.mood = selectedMood
    if (allowBookings !== (config?.allowBookings ?? false)) patch.allowBookings = allowBookings
    if (allowOrders !== (config?.allowOrders ?? false)) patch.allowOrders = allowOrders
    if (allowPromptOverwrite !== (config?.allowPromptOverwrite ?? true)) patch.allowPromptOverwrite = allowPromptOverwrite
    if (Object.keys(patch).length === 0) {
      return
    }
    update.mutate(patch)
  }

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-[var(--color-border-subtle)]">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="h-4 w-4 text-purple-400" />
          Agent Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-5 space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
          </div>
        ) : (
          <>
            {/* Greeting */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Greeting
              </label>
              <textarea
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] px-3 py-2 text-sm text-[var(--color-text-primary)] resize-none focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                placeholder="Hello! You've reached…"
              />
              <p className="text-xs text-[var(--color-text-muted)]">
                The first thing callers hear when they call.
              </p>
            </div>

            {/* Mood */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Mood
              </label>
              <div className="flex flex-wrap gap-2">
                {MOOD_OPTIONS.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => setSelectedMood(m.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedMood === m.value
                        ? 'bg-purple-600 border-purple-600 text-white'
                        : 'border-[var(--color-border-subtle)] text-[var(--color-text-secondary)] hover:border-purple-500/50 hover:text-purple-400'
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[var(--color-text-muted)]">
                Shapes the personality and tone of every call.
              </p>
            </div>

            {/* Phone workflows */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Phone workflows
              </label>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                      <CalendarCheck className="h-4 w-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--color-text-primary)]">
                        Allow phone reservations
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                        Lets the agent check table availability and create reservation requests.
                      </p>
                    </div>
                  </div>
                  <AgentToggle checked={allowBookings} onChange={() => setAllowBookings((v) => !v)} />
                </div>

                <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                      <ShoppingBag className="h-4 w-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--color-text-primary)]">
                        Allow phone menu orders
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                        Lets the agent fetch the live menu and create pickup or delivery order requests.
                      </p>
                    </div>
                  </div>
                  <AgentToggle checked={allowOrders} onChange={() => setAllowOrders((v) => !v)} />
                </div>
              </div>
              <p className="text-xs text-[var(--color-text-muted)]">
                Saving updates the agent tool access. Prompt updates depend on the prompt ownership setting below.
              </p>
            </div>

            {/* Prompt ownership */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Prompt ownership
              </label>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] p-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                    <ShieldCheck className="h-4 w-4 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-primary)]">
                      Allow backend to overwrite ElevenLabs prompt
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      Turn this off when the prompt is managed directly in ElevenLabs. Sync will still refresh tools and knowledge.
                    </p>
                  </div>
                </div>
                <AgentToggle checked={allowPromptOverwrite} onChange={() => setAllowPromptOverwrite((v) => !v)} />
              </div>
              {!allowPromptOverwrite && (
                <p className="text-xs text-amber-500">
                  Backend prompt generation and call-time prompt overrides are disabled for this agent.
                </p>
              )}
            </div>

            {/* Voice */}
            <div className="space-y-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
                Voice
              </label>
              <div className="grid grid-cols-1 gap-2">
                {VOICE_OPTIONS.map((v) => {
                  const isSelected = selectedVoice === v.voiceId
                  const isPlaying = previewPlaying === v.voiceId
                  const isLoadingVoice = previewLoading === v.voiceId
                  return (
                    <button
                      key={v.voiceId}
                      onClick={() => setSelectedVoice(v.voiceId)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                        isSelected
                          ? 'border-purple-500/50 bg-purple-500/10'
                          : 'border-[var(--color-border-subtle)] hover:border-purple-500/30'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">{v.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400">{v.tag}</span>
                          <span className="text-[10px] text-[var(--color-text-muted)]">{v.accent} · {v.gender}</span>
                        </div>
                        <p className="text-xs text-[var(--color-text-muted)] truncate mt-0.5">{v.description}</p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          playVoicePreview(v.voiceId, previewPlaying, setPreviewLoading, setPreviewPlaying)
                        }}
                        className="shrink-0 p-1.5 rounded-md hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-purple-400 transition-colors"
                        title={isPlaying ? 'Stop preview' : 'Play preview'}
                      >
                        {isLoadingVoice ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : isPlaying ? (
                          <SoundWave />
                        ) : (
                          <Volume2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </button>
                  )
                })}
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={update.isPending}
              className="bg-purple-600 hover:bg-purple-500 text-white w-full"
              size="sm"
            >
              {update.isPending ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving…</>
              ) : (
                'Save Changes'
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ── Test Agent Widget ───────────────────────────────────────────

type CallState = 'idle' | 'connecting' | 'connected' | 'error'
type AgentMode = 'listening' | 'speaking'

function TestAgentWidget({ agentId }: { agentId: string }) {
  const [callState, setCallState] = useState<CallState>('idle')
  const [agentMode, setAgentMode] = useState<AgentMode>('listening')
  const [transcript, setTranscript] = useState<{ role: 'agent' | 'user'; text: string }[]>([])
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const convRef = useRef<Awaited<ReturnType<typeof Conversation.startSession>> | null>(null)

  const startCall = useCallback(async () => {
    setCallState('connecting')
    setErrorMsg(null)
    setTranscript([])
    try {
      const token = storage.getToken()
      if (!token) throw new Error('Not authenticated')
      const wsUrl = `${STRAPI_URL.replace(/^http/, 'ws')}/api/she-answers/ws?token=${token}`

      const conv = await Conversation.startSession({
        signedUrl: wsUrl,
        onConnect: () => setCallState('connected'),
        onDisconnect: () => { setCallState('idle'); convRef.current = null },
        onModeChange: ({ mode }) => setAgentMode(mode as AgentMode),
        onMessage: ({ message, source }: { message: string; source: 'user' | 'ai' }) => {
          setTranscript((prev) => [...prev, { role: source === 'ai' ? 'agent' : 'user', text: message }])
        },
        onError: (msg: string) => {
          setErrorMsg(msg)
          setCallState('error')
        },
      })
      convRef.current = conv
    } catch (err: any) {
      setErrorMsg(err?.message ?? 'Failed to start session')
      setCallState('error')
    }
  }, [agentId])

  const stopCall = useCallback(async () => {
    await convRef.current?.endSession()
    convRef.current = null
    setCallState('idle')
  }, [])

  return (
    <Card className="border-purple-500/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Mic className="h-4 w-4 text-purple-400" />
          Test Agent (Browser)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-[var(--color-text-muted)]">
          Talk to your agent directly from the browser — no phone call or credits needed.
        </p>

        {/* Status + controls */}
        <div className="flex items-center gap-3">
          {callState === 'idle' || callState === 'error' ? (
            <Button
              onClick={startCall}
              className="bg-purple-600 hover:bg-purple-500 text-white"
              size="sm"
            >
              <Mic className="h-3.5 w-3.5 mr-1.5" />
              Start Test Call
            </Button>
          ) : callState === 'connecting' ? (
            <Button disabled size="sm" className="bg-purple-600/60 text-white">
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              Connecting…
            </Button>
          ) : (
            <>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20">
                {agentMode === 'speaking' ? (
                  <>
                    <SoundWave />
                    <span className="text-xs text-purple-400 font-medium">Agent speaking</span>
                  </>
                ) : (
                  <>
                    <span className="h-2 w-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs text-green-400 font-medium">Listening</span>
                  </>
                )}
              </div>
              <Button
                onClick={stopCall}
                variant="secondary"
                size="sm"
                className="text-red-400 border-red-500/30 hover:border-red-500/50"
              >
                <MicOff className="h-3.5 w-3.5 mr-1.5" />
                End Call
              </Button>
            </>
          )}
        </div>

        {errorMsg && (
          <p className="text-xs text-red-400">{errorMsg}</p>
        )}

        {/* Live transcript */}
        {transcript.length > 0 && (
          <div className="space-y-2 max-h-48 overflow-y-auto pt-1">
            {transcript.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'agent' ? '' : 'flex-row-reverse'}`}>
                <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${
                  msg.role === 'agent' ? 'bg-purple-500/20' : 'bg-[var(--color-bg-elevated)]'
                }`}>
                  {msg.role === 'agent'
                    ? <Bot className="h-3.5 w-3.5 text-purple-400" />
                    : <User className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                  }
                </div>
                <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-xs ${
                  msg.role === 'agent'
                    ? 'bg-purple-500/10 text-[var(--color-text-primary)]'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Sub-components ─────────────────────────────────────────────

function PhoneNumberCard({
  phoneNumber,
  onSync,
  onDeprovision,
  isSyncing,
  isDeprovisioning,
}: {
  phoneNumber?: string | null
  onSync: () => void
  onDeprovision: () => void
  isSyncing: boolean
  isDeprovisioning: boolean
}) {
  const [copied, setCopied] = useState(false)
  const hasPhoneNumber = !!phoneNumber

  function copyPhone() {
    if (!phoneNumber) return
    navigator.clipboard.writeText(phoneNumber)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card className="border-purple-500/30 bg-purple-500/5">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-purple-400 mb-1">
              Your SheAnswers Number
            </p>
            {hasPhoneNumber ? (
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-[var(--color-text-primary)] tracking-wide">
                  {phoneNumber}
                </span>
                <button
                  onClick={copyPhone}
                  className="p-1.5 rounded-md hover:bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                  title="Copy number"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-400" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
            ) : (
              <p className="text-lg font-semibold text-[var(--color-text-primary)]">
                Browser test mode
              </p>
            )}
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              {hasPhoneNumber
                ? 'Add this number to your Google listing, website, and social profiles.'
                : 'No phone number is assigned yet. You can still test and configure the agent from the browser.'}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${
                hasPhoneNumber
                  ? 'bg-green-500/10 border-green-500/20'
                  : 'bg-amber-500/10 border-amber-500/20'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full animate-pulse ${
                  hasPhoneNumber ? 'bg-green-400' : 'bg-amber-400'
                }`}
              />
              <span className={`text-xs font-medium ${hasPhoneNumber ? 'text-green-400' : 'text-amber-400'}`}>
                {hasPhoneNumber ? 'Active' : 'Test only'}
              </span>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4 pt-4 border-t border-[var(--color-border-subtle)]">
          <Button
            variant="secondary"
            size="sm"
            onClick={onSync}
            disabled={isSyncing}
          >
            {isSyncing ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            )}
            Sync Knowledge Base
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onDeprovision}
            disabled={isDeprovisioning}
            className="text-red-400 hover:text-red-300 border-red-500/30 hover:border-red-500/50"
          >
            {isDeprovisioning ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <PowerOff className="h-3.5 w-3.5 mr-1.5" />
            )}
            Disable
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ── Stats Row ───────────────────────────────────────────────────

function SheAnswersStatsRow({
  conversations,
  isLoading,
}: {
  conversations: SheAnswersConversation[]
  isLoading: boolean
}) {
  const bookingsQuery = useBookings({ source: 'she_answers', pageSize: 1 })
  const totalCalls = conversations.length
  const successfulCalls = conversations.filter(
    (c) => c.call_successful === 'success' || c.call_successful === 'true',
  ).length
  const avgDuration =
    conversations.length > 0
      ? Math.round(
          conversations.reduce((sum, c) => sum + c.call_duration_secs, 0) / conversations.length,
        )
      : 0
  const bookingsCount = (bookingsQuery.data as any)?.meta?.pagination?.total ?? 0

  if (isLoading) return null

  const cards = [
    { label: 'Total Calls', value: String(totalCalls), icon: Phone, color: '#9b7fe8' },
    { label: 'Successful Calls', value: String(successfulCalls), icon: CheckCircle2, color: '#7a9e7e' },
    { label: 'Avg Call Duration', value: formatDuration(avgDuration), icon: Clock, color: '#e8a838' },
    { label: 'Bookings via SheAnswers', value: String(bookingsCount), icon: CalendarCheck, color: '#c0614a' },
  ]

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map(({ label, value, icon: Icon, color }) => (
        <Card key={label} className="p-4">
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center mb-2"
            style={{ backgroundColor: `${color}20` }}
          >
            <Icon className="h-4 w-4" style={{ color }} />
          </div>
          <div className="text-2xl font-bold text-[var(--color-text-primary)]">{value}</div>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{label}</p>
        </Card>
      ))}
    </div>
  )
}

// ── Conversation Sidebar ─────────────────────────────────────────

function ConversationSidebar({
  conversationId,
  onClose,
}: {
  conversationId: string | null
  onClose: () => void
}) {
  useBodyScrollLock(!!conversationId)
  const { data: detail, isLoading } = useSheAnswersConversation(conversationId)

  const successful =
    (detail as any)?.call_successful === 'success' ||
    (detail as any)?.call_successful === 'true'

  return (
    <AnimatePresence>
      {conversationId && (
        <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[900] bg-black/60"
        onClick={onClose}
      />
      {/* Panel */}
      <motion.div
        {...slideInRight}
        className="fixed inset-0 z-[901] flex w-full max-w-md flex-col bg-[var(--color-bg-surface)] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:border-l sm:border-[var(--color-border-subtle)] sm:pt-0 sm:pb-0">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--color-border-subtle)] px-4 py-3">
          {detail ? (
            <div>
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                {(detail as any).start_time_unix_secs > 0
                  ? formatTimestamp((detail as any).start_time_unix_secs)
                  : 'Call'}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-[var(--color-text-muted)]">
                  {formatDuration((detail as any).call_duration_secs)}
                </span>
                <span className={`text-xs font-medium ${successful ? 'text-green-400' : 'text-red-400'}`}>
                  {successful ? 'Successful' : 'Unsuccessful'}
                </span>
              </div>
            </div>
          ) : (
            <span className="text-sm text-[var(--color-text-muted)]">Loading…</span>
          )}
          <button
            onClick={onClose}
            aria-label="Close conversation details"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-bg-elevated)] hover:text-[var(--color-text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 touch-pan-y space-y-5 overflow-y-auto overscroll-contain p-4">
            {/* Linked booking */}
            {(detail as any)?.linkedBooking ? (
              <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-purple-400">
                  Booking Created
                </p>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">
                    {(detail as any).linkedBooking.guestName}
                  </p>
                  <p className="text-xs text-[var(--color-text-muted)]">
                    {(detail as any).linkedBooking.date} at {(detail as any).linkedBooking.time}
                    {' · '}
                    {(detail as any).linkedBooking.people} guest
                    {(detail as any).linkedBooking.people !== 1 ? 's' : ''}
                  </p>
                </div>
                <Link
                  to={`/bookings/${(detail as any).linkedBooking.documentId}`}
                  className="inline-flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                >
                  View Booking
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            ) : (
              <div className="rounded-lg border border-[var(--color-border-subtle)] p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-1">
                  Booking
                </p>
                <p className="text-sm text-[var(--color-text-muted)]">
                  No booking was created during this call.
                </p>
              </div>
            )}

            {/* Transcript */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)] mb-3">
                Transcript
              </p>
              {detail?.transcript && detail.transcript.length > 0 ? (
                <div className="space-y-3">
                  {detail.transcript.map((msg, i) => (
                    <div key={i} className={`flex gap-2 ${msg.role === 'agent' ? '' : 'flex-row-reverse'}`}>
                      <div
                        className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          msg.role === 'agent' ? 'bg-purple-500/20' : 'bg-[var(--color-bg-elevated)]'
                        }`}
                      >
                        {msg.role === 'agent' ? (
                          <Bot className="h-3.5 w-3.5 text-purple-400" />
                        ) : (
                          <User className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                        )}
                      </div>
                      <div
                        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                          msg.role === 'agent'
                            ? 'bg-purple-500/10 text-[var(--color-text-primary)]'
                            : 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)]'
                        }`}
                      >
                        {msg.message}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[var(--color-text-muted)]">No transcript available.</p>
              )}
            </div>
          </div>
        )}
      </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

// ── Call History List ────────────────────────────────────────────

function formatDateLabel(unixSecs: number): string {
  const d = new Date(unixSecs * 1000)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  if (sameDay(d, today)) return 'Today'
  if (sameDay(d, yesterday)) return 'Yesterday'
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
}

function formatTimeOnly(unixSecs: number): string {
  return new Date(unixSecs * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  })
}

function conversationPhoneNumber(convo: SheAnswersConversation): string | null {
  return convo.phoneNumber ?? convo.linkedBooking?.guestPhone ?? convo.linkedOrder?.guestPhone ?? null
}

function getDateKey(unixSecs: number): string {
  const d = new Date(unixSecs * 1000)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function ConversationsList({
  conversations,
  isLoading,
  onSelectConversation,
}: {
  conversations: SheAnswersConversation[]
  isLoading: boolean
  onSelectConversation: (id: string) => void
}) {
  // Group by local date, sorted newest first
  const groups = (() => {
    const map = new Map<string, SheAnswersConversation[]>()
    const sorted = [...conversations].sort((a, b) => b.start_time_unix_secs - a.start_time_unix_secs)
    for (const c of sorted) {
      const key = getDateKey(c.start_time_unix_secs)
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(c)
    }
    return Array.from(map.entries()) // already insertion-ordered (newest key first)
  })()

  // Keep track of which date groups are open; today is open by default
  const todayKey = getDateKey(Date.now() / 1000)
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set([todayKey]))

  function toggleGroup(key: string) {
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <Card>
      <CardHeader className="pb-3 border-b border-[var(--color-border-subtle)]">
        <CardTitle className="text-base flex items-center gap-2">
          <PhoneCall className="h-4 w-4 text-purple-400" />
          Call History
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
          </div>
        ) : conversations.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Phone className="h-8 w-8 text-[var(--color-text-muted)] mx-auto mb-2 opacity-40" />
            <p className="text-sm text-[var(--color-text-muted)]">No calls yet.</p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Calls will appear here once customers start calling.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--color-border-subtle)]">
            {groups.map(([dateKey, convos]) => {
              const isOpen = openGroups.has(dateKey)
              const successCount = convos.filter(
                (c) => c.call_successful === 'success' || c.call_successful === 'true',
              ).length
              return (
                <div key={dateKey}>
                  {/* Date accordion header */}
                  <button
                    onClick={() => toggleGroup(dateKey)}
                    className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[var(--color-bg-elevated)] transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                        {formatDateLabel(convos[0].start_time_unix_secs)}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {convos.length} call{convos.length !== 1 ? 's' : ''}
                        {successCount > 0 && (
                          <span className="ml-1.5 text-green-400">· {successCount} successful</span>
                        )}
                      </span>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="h-4 w-4 text-[var(--color-text-muted)] shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-[var(--color-text-muted)] shrink-0" />
                    )}
                  </button>

                  {/* Calls in this group */}
                  {isOpen && (
                    <div className="divide-y divide-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)]">
                      {/* Column header */}
                      <div className="grid grid-cols-[70px_minmax(120px,1fr)_80px_80px_1fr] gap-3 px-4 py-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Time</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Phone</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Duration</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Status</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">Booking</span>
                      </div>
                      {convos.map((convo) => {
                        const successful =
                          convo.call_successful === 'success' || convo.call_successful === 'true'
                        const phoneNumber = conversationPhoneNumber(convo)
                        return (
                          <button
                            key={convo.conversation_id}
                            onClick={() => onSelectConversation(convo.conversation_id)}
                            className="w-full grid grid-cols-[70px_minmax(120px,1fr)_80px_80px_1fr] gap-3 px-4 py-2.5 text-left hover:bg-[var(--color-bg-elevated)] transition-colors"
                          >
                            <span className="text-sm text-[var(--color-text-primary)]">
                              {formatTimeOnly(convo.start_time_unix_secs)}
                            </span>
                            <span className="min-w-0 text-sm text-[var(--color-text-primary)]">
                              {phoneNumber ? (
                                <span className="inline-flex min-w-0 items-center gap-1.5">
                                  <Phone className="h-3 w-3 shrink-0 text-[var(--color-text-muted)]" />
                                  <span className="truncate">{phoneNumber}</span>
                                </span>
                              ) : (
                                <span className="text-[var(--color-text-muted)]">-</span>
                              )}
                            </span>
                            <span className="text-sm text-[var(--color-text-muted)] flex items-center gap-1">
                              <Clock className="h-3 w-3 shrink-0" />
                              {formatDuration(convo.call_duration_secs)}
                            </span>
                            <span>
                              <span
                                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                  successful
                                    ? 'bg-green-500/10 text-green-400'
                                    : 'bg-red-500/10 text-red-400'
                                }`}
                              >
                                {successful ? 'Success' : 'Failed'}
                              </span>
                            </span>
                            <span className="text-sm truncate">
                              {convo.linkedBooking ? (
                                <span className="text-purple-400">{convo.linkedBooking.guestName}</span>
                              ) : (
                                <span className="text-[var(--color-text-muted)]">—</span>
                              )}
                            </span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── Main Page ──────────────────────────────────────────────────

export default function SheAnswersPage() {
  const { data: restaurantRes, isLoading: isLoadingRestaurant } = useMyRestaurant()
  const restaurant = Array.isArray((restaurantRes as any)?.data)
    ? (restaurantRes as any).data[0]
    : null

  const isProvisioned = !!restaurant?.elevenlabsAgentId
  const phoneNumber = restaurant?.sheAnswersPhoneNumber

  const provision = useProvisionSheAnswers()
  const deprovision = useDeprovisionSheAnswers()
  const sync = useSyncSheAnswers()

  const [confirmDeprovision, setConfirmDeprovision] = useState(false)
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'settings' | 'test' | 'conversations'>('overview')

  // Lift conversations query to page level so stats row can share it (React Query deduplicates)
  const conversationsQuery = useSheAnswersConversations(
    isProvisioned && !!restaurant?.elevenlabsAgentId,
  )
  const conversations = conversationsQuery.data?.conversations ?? []

  function handleDeprovision() {
    if (!confirmDeprovision) {
      setConfirmDeprovision(true)
      setTimeout(() => setConfirmDeprovision(false), 4000)
      return
    }
    deprovision.mutate()
    setConfirmDeprovision(false)
  }

  const tabs = [
    { id: 'overview' as const, label: 'Overview', icon: Phone },
    { id: 'settings' as const, label: 'Settings', icon: Bot },
    { id: 'test' as const, label: 'Test Call', icon: Mic },
    { id: 'conversations' as const, label: 'Conversations', icon: Clock },
  ]

  return (
    <div className="relative">
      <PageHeader title="SheAnswers" description="AI voice agent for phone reservations and orders" />

      <div className="space-y-4">
        {/* Loading state */}
        {isLoadingRestaurant && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-[var(--color-text-muted)]" />
          </div>
        )}

        {/* Not provisioned — activation wizard */}
        {!isLoadingRestaurant && !isProvisioned && (
          <ActivationWizard
            onActivate={(voiceId) => provision.mutate(voiceId)}
            isPending={provision.isPending}
          />
        )}

        {/* Provisioned — phone number + actions */}
        {!isLoadingRestaurant && isProvisioned && restaurant?.elevenlabsAgentId && (
          <>
            <div className="flex gap-1 overflow-x-auto rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-bg-subtle)] p-1">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex min-w-max items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === id
                      ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] shadow-sm'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'overview' && (
              <div className="space-y-4">
                <PhoneNumberCard
                  phoneNumber={phoneNumber}
                  onSync={() => sync.mutate()}
                  onDeprovision={handleDeprovision}
                  isSyncing={sync.isPending}
                  isDeprovisioning={deprovision.isPending}
                />
              </div>
            )}

        {/* Deprovision confirmation hint */}
        {activeTab === 'overview' && confirmDeprovision && !phoneNumber && (
          <p className="text-xs text-red-400 text-center -mt-2">
            Click "Disable" again to confirm - this will delete the test agent.
          </p>
        )}
        {activeTab === 'overview' && confirmDeprovision && phoneNumber && (
          <p className="text-xs text-red-400 text-center -mt-2">
            Click "Disable" again to confirm — this will release your phone number.
          </p>
        )}

        {/* Stats row */}
        {activeTab === 'overview' && (
          <SheAnswersStatsRow
            conversations={conversations}
            isLoading={conversationsQuery.isLoading}
          />
        )}

        {/* Agent settings — greeting, voice, mood */}
        {activeTab === 'settings' && (
          <AgentSettingsCard />
        )}

        {/* Test Agent widget — only when provisioned */}
        {activeTab === 'test' && (
          <TestAgentWidget agentId={restaurant.elevenlabsAgentId} />
        )}

        {/* Conversations list — only when provisioned */}
        {activeTab === 'conversations' && (
          <ConversationsList
            conversations={conversations}
            isLoading={conversationsQuery.isLoading}
            onSelectConversation={setSelectedConversationId}
          />
        )}
          </>
        )}
      </div>

      {/* Conversation sidebar */}
      <ConversationSidebar
        conversationId={selectedConversationId}
        onClose={() => setSelectedConversationId(null)}
      />
    </div>
  )
}
